import { AfterViewInit, Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Event, IsActiveMatchOptions, NavigationStart, Router, RouterEvent, Scroll } from '@angular/router';
import { EnvironmentService } from '@galaxy/core';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { filter, map } from 'rxjs/operators';
import { siteMenu } from './site-menu';

const GALAXY_PAGE_TITLE = 'Galaxy Framework'; // Default Page title

@Component({
  selector: 'obs-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppComponent implements OnInit, AfterViewInit {
  // Site Menu content
  siteMenu = siteMenu;

  navLinkActiveMatchingOptions: IsActiveMatchOptions = {
    fragment: 'ignored',
    matrixParams: 'exact',
    paths: 'exact',
    queryParams: 'ignored',
  };

  private readonly environmentService = inject(EnvironmentService);
  private readonly title = inject(Title);
  private readonly router = inject(Router);
  private snowplowTracker = inject(ProductAnalyticsService);
  public readonly snowplowService = inject(ProductAnalyticsService);

  constructor() {
    this.getInitStateOfTitleBar();
  }

  ngOnInit(): void {
    this.snowplowTracker.initialize({
      environment: this.environmentService.getEnvironment(),
      projectUUID: '7b58828b-4175-4745-8d60-b06dc0c904f1',
      postHogID: 'phc_vP8i3vnF0B4yjzYsAUqFzZjZM1i9ONPK65G0BYq0lR',
      projectName: 'observatory',
    });

    // Update the page title on navigation
    this.router.events
      .pipe(
        filter((event: Event | RouterEvent): event is NavigationStart => event instanceof NavigationStart),
        map((navEvent: NavigationStart) => {
          // Remove query params ('?') and hash/fragment ('#') from url
          const currentUrl = navEvent.url.split('?')[0].split('#')[0];
          return currentUrl;
        }),
      )
      .subscribe((currentUrl) => {
        this.updateTitleBarFromUrl(currentUrl);
      });

    // Scroll to top on nav, or scroll to anchor link if exists
    // https://github.com/angular/angular/issues/24547#issuecomment-630736014
    this.router.events.pipe(filter((e): e is Scroll => e instanceof Scroll)).subscribe((e) => {
      if (e.anchor) {
        window.setTimeout(() => {
          if (e.anchor) document.getElementsByName(e.anchor)[0]?.scrollIntoView();
        }, 200);
      } else {
        const selector = document.querySelector('.mat-sidenav-content');
        if (selector) selector.scrollTop = 0;
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
    });
  }

  ngAfterViewInit(): void {
    this.scrollToActiveNavItem();
  }

  /**
   * Scroll the nav to the active nav item on first load
   */
  private scrollToActiveNavItem(): void {
    const delay = 200;
    let tries = 20;

    function findAndScrollToActiveNavItem(): void {
      const elementList = document.querySelectorAll('.nav-link-active');
      window.setTimeout(() => {
        tries--;
        if (elementList && elementList.length) {
          const element = elementList[0] as HTMLElement;
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (tries > 0) {
          findAndScrollToActiveNavItem();
        }
      }, delay);
    }
    findAndScrollToActiveNavItem();
  }

  /**
   * Updates the title bar of the page according to the passed in
   * name. Displays it as <NAME> - Galaxy Framework. If name is blank,
   * then just display Galaxy Framework
   * @param name Name of the page currently on
   */
  updateTitleBar(name: string): void {
    let pageTitle = GALAXY_PAGE_TITLE;
    if (name) {
      pageTitle = `${name} - ${pageTitle}`;
    }
    this.title.setTitle(pageTitle);
  }

  /**
   * Updates the title bar based on the url passed in. If no active route
   * is found, we will update the title bar to indicate we are on the home tab
   * @param navUrl Url to update the title of
   */
  private updateTitleBarFromUrl(navUrl: string): void {
    // Set homepage and exit early
    if (navUrl === '/') {
      this.updateTitleBar('Home');
      return;
    }

    // Get the name of the page via the url
    const foundActiveLink = this.siteMenu.some((route) => {
      return route.links.some((link) => {
        if (link.url === navUrl) {
          this.updateTitleBar(link.name);
          return true;
        }
        return false;
      });
    });

    // If we did not find anything, return a blank string
    if (!foundActiveLink) {
      this.updateTitleBar('');
    }
  }

  /**
   * Updates the title bar based on the current active route.
   */
  private getInitStateOfTitleBar(): void {
    this.updateTitleBarFromUrl(window.location.pathname);
  }
}
